/**
 * ChatInput — Rich input area for Ask Argus
 *
 * PR-AAU-B (Ask Argus Pass 1) — the composer is the single source of
 * truth for per-turn toggle state that the parent page serializes into
 * the chat request body. Locally-tracked state lives here; the parent
 * receives it via the `context` payload on each submit and folds it into
 * the POST.
 *
 * Toggles owned by this component:
 *   - Thinking Mode  (Quick / Standard / Deep)   →  toggles.thinkingMode
 *   - Web Search     (Off / On)                  →  toggles.webSearch
 *   - Knowledge Mode (Library Only / Library +AI)→  toggles.knowledgeMode
 *
 * Additional features:
 *   - Auto-growing textarea
 *   - Slash command menu
 *   - Single unified `Attach` button (PDF/DOCX/XLSX/CSV/TXT/MD/JSON +
 *     PNG/JPEG/GIF/WebP/SVG) — replaces the prior file + image split
 *   - Agent selector
 *   - Model selector (4 founder-locked brand labels, with Argus Pro
 *     greyed-out today)
 *   - Citations [ON/OFF] toggle — session-scoped, sits next to the send
 *     row so it's always visible
 *   - Submit button with keyboard shortcut hint
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { FileUploadDialog } from './file-upload-dialog';
import {
  Send, Paperclip, Slash, ChevronDown, Zap, Brain, Sparkles,
  Globe, BookOpen, BookMarked, Quote, EyeOff, AtSign, X,
  Bot, Pin,
} from 'lucide-react';
import { getIconByName } from '@/components/shared/icon-picker';
import { TokenCountMeter } from './token-count-meter';
import { makeComposerPasteHandler } from './composer-paste-image-handler';
// feat/multi-agent-in-chat — @mention autocomplete
import { MentionMenu, type MentionCandidate } from './mention-menu';
import {
  useMentionCandidates,
  type AttachedToolItem,
} from './use-mention-candidates';
import { detectMentionInProgress } from './mention-parser';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useComposerAgents,
  resolveComposerSelection,
  type ComposerAgentRow,
} from './use-composer-agents';
import { useCitationVisibility } from '@/contexts/citation-visibility-context';
import {
  expandSlashCommandForSubmit,
  filterSlashCommands,
  insertSlashCommandToken,
} from './slash-commands';
import {
  DEFAULT_TOGGLES,
  type ThinkingMode,
  type KnowledgeMode,
  type ChatToggles,
  type ChatSubmitContext as HelpersChatSubmitContext,
} from './chat-input.helpers';

// ── Thinking Modes ──────────────────────────────────────────────────────────

const THINKING_MODES: Record<ThinkingMode, { label: string; icon: React.ElementType; description: string; color: string }> = {
  quick: { label: 'Quick', icon: Zap, description: 'Fast responses, less reasoning', color: 'text-green-500' },
  standard: { label: 'Standard', icon: Brain, description: 'Balanced speed and depth', color: 'text-blue-500' },
  deep: { label: 'Deep', icon: Sparkles, description: 'Extended reasoning for complex queries', color: 'text-purple-500' },
};

// ── Web Search mode (per-turn toggle, separate from the chat-tools-strip
//     side-panel toggle which scopes to the whole conversation) ─────────────

const WEB_SEARCH_OPTIONS: Array<{ value: boolean; label: string; description: string }> = [
  { value: false, label: 'Off', description: 'Do not search the web for this turn' },
  { value: true, label: 'On', description: 'Allow the model to call `web_search` for fresh info' },
];

// ── Knowledge Mode ──────────────────────────────────────────────────────────

const KNOWLEDGE_MODES: Record<KnowledgeMode, { label: string; icon: React.ElementType; description: string }> = {
  library_only: {
    label: 'Library Only',
    icon: BookMarked,
    description: 'Answer strictly from documents the tenant has uploaded',
  },
  library_plus_ai: {
    label: 'Library + AI',
    icon: BookOpen,
    description: 'Library first, but may use general training knowledge with [AI] citations',
  },
};

// ── Unified picker helpers (askargus bug #7) ────────────────────────────────

/**
 * Resolve a composer-agent icon name (e.g. 'bot') to a small inline lucide
 * glyph. Agent icons are stored as lucide names; the bare string would render
 * the literal text ("bot") if dropped into the DOM, so we resolve it here and
 * fall back to the Bot glyph for unknown / empty names.
 */
function PickerRowIcon({ name, className }: { name: string; className?: string }) {
  const Icon = getIconByName(name) ?? Bot;
  return <Icon className={cn('h-4 w-4 shrink-0', className)} />;
}

/** One row inside the unified composer picker dropdown. */
function PickerRow({
  row,
  selected,
  onSelect,
}: {
  row: ComposerAgentRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      onClick={onSelect}
      className={cn('gap-2', selected && 'bg-accent')}
    >
      <PickerRowIcon name={row.icon} className="text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{row.name}</span>
          {row.revLabel && (
            <span className="text-[10px] text-muted-foreground font-mono">
              {row.revLabel}
            </span>
          )}
          {row.pinned && <Pin className="h-3 w-3 text-muted-foreground/60 shrink-0" />}
        </div>
        {row.description && (
          <div className="text-[10px] text-muted-foreground truncate">
            {row.description}
          </div>
        )}
      </div>
    </DropdownMenuItem>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export type ChatSubmitContext = HelpersChatSubmitContext;

interface ChatInputProps {
  onSubmit: (message: string, context?: ChatSubmitContext) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * feat/multi-agent-in-chat — per-conversation attached agents,
   * connectors, and skills. Passed in by the parent (Ask Argus page)
   * after fetching `/conversations/:id/attached-tools`. When null,
   * the @mention dropdown shows all agents the tenant has access to.
   */
  attachedTools?: AttachedToolItem[] | null;
  /**
   * R1 P1 #4 — when true, the composer swaps the Send button for a
   * Stop button. Click invokes `onStop`. The parent owns the
   * AbortController and is responsible for cancelling the in-flight
   * fetch + collapsing UI state on stop.
   */
  isStreaming?: boolean;
  onStop?: () => void;
  /**
   * Wave 2C — paste-image handler callback. When the user pastes an
   * image from clipboard, extracted File objects are handed to this
   * callback. The parent should open the upload dialog / add to queue.
   * When undefined, pasted images trigger `setAttachOpen(true)` with
   * files queued in the component's own pending state.
   */
  onPastedFiles?: (files: File[]) => void;
  /**
   * Publishes the composer's CURRENT selection (agent, model, thinking mode,
   * toggles) to the parent whenever it changes.
   *
   * WHY: a turn can be started from six places that are not the composer —
   * a follow-up chip, Regenerate, Retry, Edit-resend, Continue, and a starter
   * prompt. Those callers have no submit context, so the parent used to fall
   * back to DEFAULT_TOGGLES and drop agent/model entirely: a user who selected
   * "Library Only" and then clicked a follow-up chip silently got
   * "Library + AI" while the toolbar still displayed their original choice.
   * Publishing the live state lets the parent use it as the fallback instead of
   * defaults, so every send path honours what the toolbar is showing.
   */
  onContextChange?: (context: ChatSubmitContext) => void;
}

export function ChatInput({
  onSubmit,
  disabled = false,
  placeholder,
  attachedTools = null,
  isStreaming = false,
  onStop,
  onPastedFiles,
  onContextChange,
}: ChatInputProps) {
  const [input, setInput] = useState('');

  // ── UNIFIED Agent picker (askargus bug #7) ───────────────────────────────
  // The composer used to render TWO parallel pickers — a tenant-agent "Select
  // Agent" dropdown AND a separate four-brand "Select Model" dropdown. The
  // locked ASK-ARGUS-UI-SPEC §B.3.3 mandates ONE picker with two grouped
  // sections: "Models" (published SYSTEM agents — the chat brains, rendered
  // under the audience-aware Models label) and "Your agents" (tenant
  // published agents). `useComposerAgents` reads the server-side unified
  // endpoint that already filters drafts / disabled / soft-deleted / QA-junk
  // rows (also closes bug #8 — no junk drafts in the picker).
  //
  // Selection drives BOTH the agent id (BUILTIN_AGENTS slug for system rows,
  // UUID for tenant rows) and — for a system "Model" — the engine `brand`, so
  // picking a different Model genuinely changes the engine at dispatch, not
  // just a label. See resolveComposerSelection + handleSubmit below.
  const { models: systemModels, agents: tenantAgents, all: pickerRows } =
    useComposerAgents();
  const [selectedPickId, setSelectedPickId] = useState<string | null>(null);
  const selectedPick: ComposerAgentRow | null =
    pickerRows.find((r) => r.id === selectedPickId) ?? pickerRows[0] ?? null;
  // Default to the first row once the list loads (Ask Argus is server-sorted
  // first in the "Models" group, so it wins as the default chat brain).
  useEffect(() => {
    if (!selectedPickId && pickerRows.length > 0) {
      setSelectedPickId(pickerRows[0].id);
    }
  }, [pickerRows, selectedPickId]);

  // ── Per-turn toggles (PR-AAU-B) ──────────────────────────────────────────
  const [thinkingMode, setThinkingMode] = useState<ThinkingMode>(DEFAULT_TOGGLES.thinkingMode);
  const [webSearch, setWebSearch] = useState<boolean>(DEFAULT_TOGGLES.webSearch);
  const [knowledgeMode, setKnowledgeMode] = useState<KnowledgeMode>(DEFAULT_TOGGLES.knowledgeMode);

  // Publish the live composer selection so turns that DON'T originate here
  // (follow-up chip, Regenerate, Retry, Edit, Continue, starter prompt) can
  // reuse it instead of silently reverting to DEFAULT_TOGGLES. Runs on every
  // change to the four things a send actually carries.
  useEffect(() => {
    if (!onContextChange) return;
    const { agent, model } = resolveComposerSelection(selectedPick);
    onContextChange({
      agent,
      model,
      thinkingMode,
      toggles: { thinkingMode, webSearch, knowledgeMode },
    });
  }, [onContextChange, selectedPick, thinkingMode, webSearch, knowledgeMode]);

  // ── Citation visibility (session-scoped, exposed via context) ────────────
  const { citationsVisible, toggle: toggleCitations } = useCitationVisibility();

  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashFilter, setSlashFilter] = useState('');

  // feat/multi-agent-in-chat — @mention autocomplete state. The
  // dropdown opens whenever the cursor is inside a partial mention
  // (detected by `detectMentionInProgress`). The user picks a
  // candidate with Enter / Tab / click; the selected candidate is
  // spliced into the textarea at the right position. Highlight index
  // is reset whenever the candidate list shape changes.
  const [mentionPartial, setMentionPartial] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number>(-1);
  const [mentionHighlightIdx, setMentionHighlightIdx] = useState(0);
  const { candidates: mentionCandidates } = useMentionCandidates(
    mentionPartial ?? '',
    attachedTools,
  );
  const showMentionMenu = mentionPartial !== null;
  useEffect(() => {
    // Reset highlight when the visible candidate set changes.
    setMentionHighlightIdx(0);
  }, [mentionCandidates.length, mentionPartial]);
  // Single unified attach dialog (PR-AAU-B). The dialog itself already
  // accepts every type we need via `mode='file'` — extending the file
  // type list lives in `file-upload-dialog.tsx`.
  const [attachOpen, setAttachOpen] = useState(false);

  // ── Wave 2C: Paste-image handler ─────────────────────────────────────────
  // When an image is pasted, we either call the parent's `onPastedFiles`
  // callback or fall back to opening the built-in attach dialog and queuing
  // the files. Either way we show an informative toast because the backend
  // upload pipeline is not yet wired (audit §7 limitation 8).
  const [pendingPastedFiles, setPendingPastedFiles] = useState<File[]>([]);

  const handlePastedFiles = useCallback((files: File[]) => {
    if (onPastedFiles) {
      onPastedFiles(files);
    } else {
      // Fall back: surface the attach dialog with the pasted files queued.
      setPendingPastedFiles(files);
      setAttachOpen(true);
    }
    const count = files.length;
    toast.info(
      `${count} image${count === 1 ? '' : 's'} captured from clipboard. ` +
      `File attachments are not yet sent to the model — will be included when backend ships.`,
    );
  }, [onPastedFiles]);

  const handlePaste = makeComposerPasteHandler(handlePastedFiles);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  // Issue #1323 — single-submit guard. Prevents one prompt from being
  // submitted twice when either (a) the user presses Enter and clicks the
  // send button in the same gesture, (b) Cmd/Ctrl+Enter is intercepted by
  // a local AND a global handler, or (c) React StrictMode double-invokes
  // the handler in dev. The flag is cleared on the next animation frame so
  // a genuine second prompt typed in by the user is still accepted.
  const submitInFlightRef = useRef(false);

  // bug #18 — text-readable attachment content, read client-side in onAttach
  // and prepended to the next message so it actually reaches the model. Cleared
  // after each send. Non-text files (image/PDF) still get the honest warning.
  const pendingAttachmentRef = useRef('');

  const handleSubmit = useCallback(() => {
    if (!input.trim() || disabled) return;
    if (submitInFlightRef.current) return;
    if (isStreaming) return;
    submitInFlightRef.current = true;
    // bug #31 — hold the latch long enough to swallow a rapid multi-Enter
    // BURST (the old single-rAF / 16ms window released between frame-separated
    // keypresses, so 3 fast Enters produced 2 sends). 500ms reliably covers the
    // gap before `isStreaming` flips and becomes the authoritative guard for a
    // real send; a deliberate second message naturally takes longer to type.
    setTimeout(() => {
      submitInFlightRef.current = false;
    }, 500);
    const toggles: ChatToggles = { thinkingMode, webSearch, knowledgeMode };
    // fix-wave-4 #848 + #854 — the composer now stores the LITERAL
    // slash token; we expand the recognized commands at SEND time so
    // the model still receives the natural-language template.
    let finalText = expandSlashCommandForSubmit(input.trim());
    // bug #18 — prepend any text-attachment content (read in onAttach). Cap the
    // attachment portion to leave headroom for the user text + any highlights
    // the route prepends downstream, so the combined query stays under the
    // backend's 10000-char limit.
    const attachment = pendingAttachmentRef.current;
    if (attachment) {
      const MAX_TOTAL = 9000; // ~1000 headroom under the 10000 backend cap for highlights
      const room = MAX_TOTAL - finalText.length - 2;
      let attachPart = attachment;
      if (attachPart.length > room) {
        attachPart =
          attachPart.slice(0, Math.max(0, room)) + '\n…[attachment truncated]';
        toast.warning('Attachment was truncated to fit the message size limit.');
      }
      if (attachPart.trim()) finalText = attachPart + '\n\n' + finalText;
      pendingAttachmentRef.current = '';
    }
    // askargus bug #7 — resolve the unified-picker selection into the
    // {agent, model} pair the pipeline expects:
    //   - System "Model" row → agent = slug (BUILTIN_AGENTS match, #1082)
    //                          + model = brandId (real engine dispatch).
    //   - Tenant agent row   → agent = UUID id, model = '' (default brand).
    const { agent, model } = resolveComposerSelection(selectedPick);
    onSubmit(finalText, {
      agent,
      model,
      thinkingMode,
      toggles,
    });
    setInput('');
    setShowSlashMenu(false);
  }, [
    input,
    disabled,
    isStreaming,
    onSubmit,
    selectedPick,
    thinkingMode,
    webSearch,
    knowledgeMode,
  ]);

  /**
   * Insert the chosen candidate at the current mention position. The
   * mention range (from `@` to the cursor) is replaced with
   * `@<mentionSlug> ` (trailing space so the body starts on a fresh
   * token). After insertion, the autocomplete closes and focus is
   * restored to the textarea.
   */
  const handleMentionSelect = useCallback(
    (candidate: MentionCandidate) => {
      const el = textareaRef.current;
      if (!el || mentionStart < 0) return;
      const cursor = el.selectionEnd ?? input.length;
      const before = input.slice(0, mentionStart);
      const after = input.slice(cursor);
      const inserted = `@${candidate.mentionSlug} `;
      const next = before + inserted + after;
      setInput(next);
      setMentionPartial(null);
      setMentionStart(-1);
      setTimeout(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        const pos = before.length + inserted.length;
        node.setSelectionRange(pos, pos);
      }, 0);
    },
    [input, mentionStart],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // ── Mention-menu key handling takes priority over send / slash.
    if (showMentionMenu && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionHighlightIdx((i) =>
          Math.min(mentionCandidates.length - 1, i + 1),
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionHighlightIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const pick = mentionCandidates[mentionHighlightIdx];
        if (pick) handleMentionSelect(pick);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionPartial(null);
        setMentionStart(-1);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === '/' && input === '') {
      setShowSlashMenu(true);
    }
    if (e.key === 'Escape') {
      setShowSlashMenu(false);
      setMentionPartial(null);
      setMentionStart(-1);
    }
  }, [
    handleSubmit,
    input,
    showMentionMenu,
    mentionCandidates,
    mentionHighlightIdx,
    handleMentionSelect,
  ]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);

    if (val.startsWith('/')) {
      setShowSlashMenu(true);
      setSlashFilter(val.slice(1));
    } else {
      setShowSlashMenu(false);
      setSlashFilter('');
    }

    // feat/multi-agent-in-chat — detect mid-mention state to drive
    // the @mention autocomplete dropdown.
    const caret = e.target.selectionEnd ?? val.length;
    const detected = detectMentionInProgress(val, caret);
    if (detected !== null) {
      setMentionPartial(detected.partial);
      setMentionStart(detected.mentionStart);
    } else {
      setMentionPartial(null);
      setMentionStart(-1);
    }
  }, []);

  /**
   * Slash command click — inserts the LITERAL `/<command> ` token so
   * the user can see (and edit) which command they picked. Expansion
   * to the natural-language template happens on SEND inside
   * `expandSlashCommandForSubmit`. The final message is still a
   * normal chat turn through /api/v1/ecosystem/query; there is no
   * separate dispatch route per command.
   *
   * fix-wave-4 #848 + #854 — previously this clicked-to-expand
   * directly; the user lost visibility into which slash they had
   * picked and couldn't edit the literal token.
   */
  const handleSlashSelect = (command: string) => {
    setInput(insertSlashCommandToken(command));
    setShowSlashMenu(false);
    // Defer focus so the textarea has the updated value before we set
    // the caret to the end.
    setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }, 0);
  };

  const filteredSlash = filterSlashCommands(slashFilter);

  const ThinkingIcon = THINKING_MODES[thinkingMode].icon;
  const KnowledgeIcon = KNOWLEDGE_MODES[knowledgeMode].icon;

  return (
    <div className="border-t border-border/40 bg-background shrink-0">
      <div className="relative p-4">
        {/* feat/multi-agent-in-chat — @mention autocomplete dropdown. */}
        <MentionMenu
          open={showMentionMenu}
          candidates={mentionCandidates}
          highlightedIndex={mentionHighlightIdx}
          onSelect={handleMentionSelect}
          onHighlight={setMentionHighlightIdx}
        />

        {/* Slash command popup — positioned above the input */}
        {showSlashMenu && filteredSlash.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 px-0 pb-2 z-50">
            <div className="mx-0 border border-border/40 rounded-lg bg-popover shadow-xl overflow-hidden">
              <div className="p-1.5 max-h-[240px] overflow-y-auto">
                {filteredSlash.map(cmd => (
                  <button
                    key={cmd.command}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left"
                    onClick={() => handleSlashSelect(cmd.command)}
                  >
                    <span className="text-base">{cmd.icon}</span>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">/{cmd.command}</span>
                      <span className="text-muted-foreground ml-2 truncate">{cmd.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="relative border rounded-xl bg-muted/30 focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 transition-all">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder ?? 'Ask Argus anything... (/ for commands)'}
            disabled={disabled}
            className="min-h-[44px] max-h-[200px] resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 pr-20 text-sm"
            rows={1}
          />

          {/* Send / Stop button (R1 P1 #4).
              While a stream is in flight AND the parent supplied an
              `onStop` handler, the Send button is replaced by a red
              Stop button. The user can click it to abort the in-flight
              SSE stream. The textarea stays disabled either way; the
              Stop button is always clickable. */}
          {isStreaming && onStop ? (
            <Button
              size="icon"
              onClick={onStop}
              className="absolute right-2 bottom-2 h-8 w-8 rounded-lg bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              aria-label="Stop generating"
              title="Stop generating"
              data-testid="ask-argus-stop-button"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              size="icon"
              onClick={handleSubmit}
              disabled={!input.trim() || disabled}
              className="absolute right-2 bottom-2 h-8 w-8 rounded-lg"
              aria-label="Send message"
              title="Send"
              data-testid="ask-argus-send-button"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Wave 2C: Token count meter — only shown when text is present. */}
        <TokenCountMeter
          text={input}
          className="mt-1.5"
        />

        {/* Bottom toolbar */}
        <div className="flex items-center justify-between mt-2 gap-1 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {/* Attach — single unified button (PR-AAU-B). Accepts both
                document and image MIME types via the existing dialog
                in `file` mode (the dialog's accept list covers both). */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => setAttachOpen(true)}
                    aria-label="Attach file or image"
                    title="Attach"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Attach file or image</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Slash commands */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => setShowSlashMenu(!showSlashMenu)}
                    aria-label="Slash commands"
                    title="Slash commands"
                  >
                    <Slash className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Slash commands</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* feat/multi-agent-in-chat — @mention discovery button. */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    onClick={() => {
                      const el = textareaRef.current;
                      if (!el) return;
                      const cursor = el.selectionEnd ?? input.length;
                      const before = input.slice(0, cursor);
                      const after = input.slice(cursor);
                      const needsLeadingSpace =
                        cursor > 0 && !/\s/.test(input[cursor - 1] ?? '');
                      const insert = (needsLeadingSpace ? ' ' : '') + '@';
                      const next = before + insert + after;
                      setInput(next);
                      setMentionPartial('');
                      setMentionStart(before.length + insert.length - 1);
                      setTimeout(() => {
                        const node = textareaRef.current;
                        if (!node) return;
                        node.focus();
                        const pos = before.length + insert.length;
                        node.setSelectionRange(pos, pos);
                      }, 0);
                    }}
                    aria-label="Mention an agent"
                    title="Mention an agent"
                  >
                    <AtSign className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mention an agent</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <div className="w-px h-4 bg-border mx-1" />

            {/* Citations [ON / OFF] — session-scoped (PR-AAU-B).
                Lives in the producer (here) and is consumed by the
                message bubble via `useCitationVisibility()`. */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-muted-foreground"
                    onClick={toggleCitations}
                    aria-label={
                      citationsVisible
                        ? 'Hide inline citations in message bodies'
                        : 'Show inline citations in message bodies'
                    }
                    title="Toggle inline citation markers"
                  >
                    {citationsVisible ? (
                      <Quote className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                    <span>Citations</span>
                    <span
                      className={cn(
                        'text-[10px] font-mono px-1 rounded',
                        citationsVisible
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {citationsVisible ? 'ON' : 'OFF'}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {citationsVisible
                    ? 'Inline [N] markers visible in messages'
                    : 'Inline [N] markers hidden (right panel still shows them)'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <div className="w-px h-4 bg-border mx-1" />

            {/* Unified Agent picker (askargus bug #7, ASK-ARGUS-UI-SPEC
                §B.3.3) — ONE picker on the left with two grouped sections:
                "Models" (published system agents — the chat brains) and
                "Your agents" (tenant published agents). Replaces the old
                split Agent + Model dropdowns. Selecting a Model row submits
                its slug + engine brand so the engine actually changes;
                selecting a tenant agent submits its id. */}
            {selectedPick && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-muted-foreground max-w-[180px]"
                    aria-label="Select agent or model"
                  >
                    <PickerRowIcon name={selectedPick.icon} />
                    <span className="truncate">{selectedPick.name}</span>
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80 max-h-[420px] overflow-y-auto">
                  {/* "Models" group — published system agents (the chat
                      brains). Always present (Ask Argus is seeded), but
                      guarded for robustness. */}
                  {systemModels.length > 0 && (
                    <>
                      <DropdownMenuLabel>Models</DropdownMenuLabel>
                      {systemModels.map((row) => (
                        <PickerRow
                          key={row.id}
                          row={row}
                          selected={row.id === selectedPick.id}
                          onSelect={() => setSelectedPickId(row.id)}
                        />
                      ))}
                    </>
                  )}
                  {/* "Your agents" group — tenant published agents. Only
                      rendered when the tenant has authored at least one. */}
                  {tenantAgents.length > 0 && (
                    <>
                      {systemModels.length > 0 && <DropdownMenuSeparator />}
                      <DropdownMenuLabel>Your agents</DropdownMenuLabel>
                      {tenantAgents.map((row) => (
                        <PickerRow
                          key={row.id}
                          row={row}
                          selected={row.id === selectedPick.id}
                          onSelect={() => setSelectedPickId(row.id)}
                        />
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="flex items-center gap-1 flex-wrap">
            {/* Web Search — per-turn (PR-AAU-B) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground">
                  <Globe className={cn('h-3.5 w-3.5', webSearch ? 'text-purple-500' : '')} />
                  <span>Web {webSearch ? 'On' : 'Off'}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Web Search</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {WEB_SEARCH_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={String(opt.value)}
                    onClick={() => setWebSearch(opt.value)}
                    className={cn(opt.value === webSearch && 'bg-accent')}
                  >
                    <Globe className={cn('h-4 w-4 mr-2', opt.value ? 'text-purple-500' : 'text-muted-foreground')} />
                    <div>
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.description}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Knowledge Mode — per-turn (PR-AAU-B) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground">
                  <KnowledgeIcon className={cn('h-3.5 w-3.5', knowledgeMode === 'library_plus_ai' ? 'text-blue-500' : '')} />
                  <span>{KNOWLEDGE_MODES[knowledgeMode].label}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Knowledge Mode</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(Object.entries(KNOWLEDGE_MODES) as [KnowledgeMode, typeof KNOWLEDGE_MODES[KnowledgeMode]][]).map(([key, mode]) => {
                  const ModeIcon = mode.icon;
                  return (
                    <DropdownMenuItem
                      key={key}
                      onClick={() => setKnowledgeMode(key)}
                      className={cn(key === knowledgeMode && 'bg-accent')}
                    >
                      <ModeIcon className={cn('h-4 w-4 mr-2', key === 'library_plus_ai' ? 'text-blue-500' : 'text-muted-foreground')} />
                      <div>
                        <div className="text-sm font-medium">{mode.label}</div>
                        <div className="text-xs text-muted-foreground">{mode.description}</div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Thinking mode */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground">
                  <ThinkingIcon className={cn('h-3.5 w-3.5', THINKING_MODES[thinkingMode].color)} />
                  <span>{THINKING_MODES[thinkingMode].label}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Thinking Mode</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(Object.entries(THINKING_MODES) as [ThinkingMode, typeof THINKING_MODES[ThinkingMode]][]).map(([key, mode]) => {
                  const ModeIcon = mode.icon;
                  return (
                    <DropdownMenuItem
                      key={key}
                      onClick={() => setThinkingMode(key)}
                      className={cn(key === thinkingMode && 'bg-accent')}
                    >
                      <ModeIcon className={cn('h-4 w-4 mr-2', mode.color)} />
                      <div>
                        <div className="text-sm font-medium">{mode.label}</div>
                        <div className="text-xs text-muted-foreground">{mode.description}</div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Model selector removed (askargus bug #7). The four-brand
                "Select Model" dropdown that used to live here is folded into
                the unified Agent picker on the LEFT — its "Models" group is
                the published system agents. Engines (raw LLMs) are
                sysadmin-only and never appear in the composer
                (CATALOG-ARCHITECTURE §1, §2, §8; ASK-ARGUS-UI-SPEC §B.3.3). */}

            {/* Keyboard shortcut hint */}
            <span className="text-[10px] text-muted-foreground/50 ml-1 hidden sm:inline">
              ↵ send · ⇧↵ newline
            </span>
          </div>
        </div>
      </div>

      {/* Unified attach dialog — handles both document + image MIME types.
          The existing `file` mode already covers documents; routing for
          images stays inside the dialog/uploader. Founder spec: one
          entry point, modal auto-routes by file type.
          Wave 2C: also opened when the user pastes an image from clipboard. */}
      <FileUploadDialog
        open={attachOpen}
        onClose={() => {
          setAttachOpen(false);
          // Clear any pending pasted files when the dialog is dismissed.
          setPendingPastedFiles([]);
        }}
        mode="file"
        onAttach={async (files) => {
          // Clear pending pasted files once the dialog confirms.
          setPendingPastedFiles([]);
          // bug #18 — TEXT-based attachments (.txt/.md/.csv/.json/.yaml/…) are read
          // client-side and prepended to the next message (see handleSubmit), so
          // they genuinely reach the model. Image/PDF/DOCX still need server-side
          // parsing (vision/Docling) and keep the honest "not sent" warning rather
          // than the old fake-success that produced hallucinated answers (#751).
          const TEXT_MIME =
            /^(text\/|application\/(json|xml|x-yaml|yaml|csv|x-ndjson)$)/i;
          const TEXT_EXT = /\.(txt|md|markdown|csv|tsv|json|ya?ml|log|xml)$/i;
          const MAX_FILE = 64 * 1024; // 64 KB/file — keeps the prompt bounded
          const textFiles = files.filter(
            (f) => (TEXT_MIME.test(f.type) || TEXT_EXT.test(f.name)) && f.size <= MAX_FILE,
          );
          const otherFiles = files.filter((f) => !textFiles.includes(f));
          const blocks: string[] = [];
          for (const f of textFiles) {
            try {
              const content = await f.text();
              blocks.push(`--- Attached file: ${f.name} ---\n\`\`\`\n${content}\n\`\`\``);
            } catch {
              /* unreadable file — skip */
            }
          }
          pendingAttachmentRef.current = blocks.join('\n\n');
          if (blocks.length > 0) {
            toast.success(
              `${blocks.length} text file${blocks.length === 1 ? '' : 's'} will be included with your next message.`,
            );
          }
          if (otherFiles.length > 0) {
            toast.warning(
              `${otherFiles.length} attachment${otherFiles.length === 1 ? '' : 's'} (image/PDF/large files) were NOT sent — those need server-side parsing. Paste relevant text into the prompt.`,
            );
          }
        }}
      />
    </div>
  );
}
